import _get from 'lodash/get';
import _memoize from 'lodash/memoize';

import { urlUtils } from '../../../utils';
import * as Api from '../../../weborder/Api';

const getArray = (basketSnapshot, id) => {
  const result = _get(basketSnapshot, id);
  if (!result) {
    return undefined;
  }

  return Object.values(result);
};

const getFormatAddress = (address: string) =>
  address.charAt(0) === ',' ? address : `, ${address}`;

const getCheckoutUrl = (
  order: Api.PreviousOrderDm,
  concessionStoreId: number,
): string => {
  const vrId = order.VirtualRestaurantId;
  const deliveryType =
    order.DeliveryType === Api.DeliveryType.Collection
      ? 'collection'
      : 'delivery';
  const prId = order.PhysicalRestaurantId;
  const errorParam = `?paymentError=${order.PaymentProcessingResponse}`;

  const locationHref = urlUtils.getWindowLocation('href');
  const splitURLOnQuery = locationHref.includes('#/order/')
    ? locationHref.split('#/order/')
    : locationHref.split('/order?');

  let checkoutRoute = `/order/#/checkout/${vrId}/${deliveryType}/${prId}`;
  if (concessionStoreId) {
    checkoutRoute = `${checkoutRoute}/${concessionStoreId}`;
  }
  checkoutRoute += errorParam;

  return splitURLOnQuery[0] + checkoutRoute;
};

const getCollectionRoute = (vrId, restaurantId): string =>
  `/restaurant/${vrId}/collection/${restaurantId}`;

const getDeliveryRoute = (vrId, locationId): string =>
  `/restaurant/${vrId}/delivery/${locationId}`;

const getMenuLink = _memoize(
  (order: Api.PreviousOrderDm, concessionStoreId: number): string => {
    let menuRoute = '/';
    if (order) {
      const vrId = order.VirtualRestaurantId;
      if (order.DeliveryType === Api.DeliveryType.Collection) {
        menuRoute = getCollectionRoute(vrId, order.PhysicalRestaurantId);
      } else {
        menuRoute = getDeliveryRoute(vrId, order.DeliveryLocationId);
      }
      if (concessionStoreId) {
        menuRoute += `/${concessionStoreId}`;
      }
    }

    return menuRoute;
  },
);

const getVoucherCode = (basketSnapshot) => _get(basketSnapshot, 'voucher.code');

const getVoucherItemList = (basketSnapshot) =>
  getArray(basketSnapshot, 'voucher.items');

const getVoucherAmount = (basketSnapshot) => {
  const voucherItemList = getVoucherItemList(basketSnapshot);
  if (!voucherItemList) {
    return 0;
  }

  const amount = voucherItemList.reduce((sum: number, item) => {
    // eslint-disable-next-line no-param-reassign
    sum += _get(item, 'Amount');
    return sum;
  }, 0);
  return amount;
};

const getVoucherDescription = (basketSnapshot) => {
  const voucherItemList = getVoucherItemList(basketSnapshot);
  if (!voucherItemList) {
    return [];
  }

  return voucherItemList.map(
    (item: Api.VoucherBasketItemDm) => item.Description,
  );
};

const getServiceCharge = (basketSnapshot) => {
  const defaultServiceChargeData = {
    amountExcludingProcessingFee: 0,
    displayAsOneWithProcessingFee: false,
    isEnabled: false,
  };

  if (
    basketSnapshot.serviceCharge.status === 'none' ||
    !basketSnapshot.serviceCharge.config
  ) {
    return defaultServiceChargeData;
  }

  const {
    AmountExcludingProcessingFee: amountExcludingProcessingFee,
    DisplayAsOneWithProcessingFee: displayAsOneWithProcessingFee,
    IsEnabled: isEnabled,
  } = basketSnapshot.serviceCharge.config;

  return {
    amountExcludingProcessingFee,
    displayAsOneWithProcessingFee,
    isEnabled,
  };
};

const getSummary = (basketSnapshot) => {
  const getItemOptions = (options) => {
    if (!options) {
      return undefined;
    }

    const optionCodes = Object.values(options).map(
      (option: any) => option.Name,
    );
    return { options: optionCodes };
  };

  const basketItems = getArray(basketSnapshot, 'basketItems');
  const basketSummary = basketItems.map((item: any) => ({
    item,
    ...getItemOptions(item.item.Options),
  }));

  return basketSummary;
};

const getBasketSummary = (
  basketSnapshot: WebOrder.State.BasketSnapshot,
): any => {
  if (!basketSnapshot) return undefined;

  const summary = getSummary(basketSnapshot);
  const voucherAmount = getVoucherAmount(basketSnapshot);
  const voucherCode = getVoucherCode(basketSnapshot);
  const voucherDescription = getVoucherDescription(basketSnapshot);
  const serviceCharge = getServiceCharge(basketSnapshot);

  return {
    ...basketSnapshot,
    currencyNumber: basketSnapshot.total?.currency,
    tipAmount: basketSnapshot.total?.order.TipAmount,
    totalAmount: basketSnapshot.total?.totalToPay,
    summary,
    voucher: {
      voucherAmount,
      voucherCode,
      voucherDescription,
    },
    serviceCharge,
  };
};

export const OrderCompleteUtils = {
  getBasketSummary,
  getCheckoutUrl,
  getFormatAddress,
  getMenuLink,
  getServiceCharge,
  getSummary,
  getVoucherAmount,
  getVoucherDescription,
};
